import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, AlertCircle, UserPlus } from 'lucide-react';
import authService from '../services/authService';
import { APP_BRAND } from '../constants/branding';

interface LoginFormProps {
  onLogin: (email: string, password: string) => Promise<boolean>;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const success = await onLogin(formData.email, formData.password);
      if (!success) {
        setError('Email o contraseña incorrectos. Por favor verifica tus credenciales.');
      }
    } catch (error) {
      console.error('❌ Error en login:', error);
      let errorMessage = 'Error al iniciar sesión. Por favor intenta de nuevo.';

      if (error.message) {
        if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Por favor confirma tu email antes de iniciar sesión.';
        } else if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Email o contraseña incorrectos.';
        } else if (error.message.includes('Email rate limit exceeded')) {
          errorMessage = 'Demasiados intentos. Por favor espera unos minutos.';
        } else if (error.message.includes('Network')) {
          errorMessage = 'Error de conexión. Verifica tu internet.';
        } else {
          errorMessage = error.message;
        }
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await authService.resetPassword(forgotPasswordEmail);
      setForgotPasswordSent(true);
    } catch (error) {
      console.error('Error al enviar email de recuperación:', error);
      setError('Error al enviar el email de recuperación. Por favor intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!registerData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('El formato del email no es válido');
      return;
    }

    if (registerData.name.trim().length < 3) {
      setError('El nombre debe tener al menos 3 caracteres');
      return;
    }

    setIsLoading(true);

    try {
      // The password is set later by the user through the invite email that
      // the approval flow sends. We used to store btoa(password) here, which
      // is not encryption — see migration 20260917100000.
      const { registrationRequestsService } = await import('../services/supabaseService');
      await registrationRequestsService.create({
        name: registerData.name.trim(),
        email: registerData.email.trim().toLowerCase()
      });

      setRegisterSuccess(true);
      setRegisterData({ name: '', email: '', password: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error al enviar solicitud:', error);
      if (error.message && (error.message.includes('duplicate') || error.message.includes('unique'))) {
        setError('Este email ya tiene una solicitud de registro pendiente o ya está registrado.');
      } else {
        setError('Error al enviar la solicitud de registro. Por favor intenta de nuevo.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordSent(false);
  };

  const resetRegister = () => {
    setShowRegister(false);
    setRegisterData({ name: '', email: '', password: '', confirmPassword: '' });
    setError('');
  };

  // Register Form
  if (showRegister) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
          <div className="text-center mb-8">
            <img
              src={APP_BRAND.logoPath}
              alt={APP_BRAND.logoAlt}
              className="w-16 h-16 mx-auto mb-4 rounded-xl shadow-sm"
            />
            <h1 className="text-2xl font-bold text-gray-900">Crear Cuenta</h1>
            <p className="text-gray-600 mt-2">Regístrate en el CRM</p>
          </div>

          {registerSuccess ? (
            <div className="text-center space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <UserPlus className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-base font-semibold text-green-900 mb-2">Solicitud enviada</h3>
                <p className="text-sm text-green-700">
                  Un administrador revisará tu solicitud y recibirás un email cuando tu cuenta sea aprobada. Revisa tu bandeja de entrada en las próximas 24–48 horas.
                </p>
              </div>
              <button
                onClick={() => { setShowRegister(false); setRegisterSuccess(false); }}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Volver al inicio de sesión
              </button>
            </div>
          ) : (
          <form onSubmit={handleRegister} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre completo
              </label>
              <input
                type="text"
                required
                value={registerData.name}
                onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                placeholder="Tu nombre completo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  required
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="tu@email.com"
                />
              </div>
            </div>

            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
              No pedimos contraseña aquí. Cuando un administrador apruebe tu
              solicitud recibirás un email con un enlace para configurar la
              tuya y acceder al CRM.
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Enviando solicitud...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5 mr-2" />
                    Solicitar registro
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={resetRegister}
                className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Volver al login
              </button>
            </div>
          </form>
          )}
        </div>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="/logo-ceroideas.png" 
              alt="Cero Ideas Logo" 
              className="w-16 h-16 mx-auto mb-4 rounded-xl shadow-sm"
            />
            <h1 className="text-2xl font-bold text-gray-900">Recuperar Contraseña</h1>
            <p className="text-gray-600 mt-2">
              {forgotPasswordSent 
                ? 'Te hemos enviado un email con las instrucciones'
                : 'Ingresa tu email para recuperar tu contraseña'
              }
            </p>
          </div>

          {forgotPasswordSent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Email enviado</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Hemos enviado las instrucciones de recuperación a <strong>{forgotPasswordEmail}</strong>
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                  <p className="text-xs text-yellow-800">
                    <strong>Importante:</strong> El enlace de recuperación expira en <strong>5 minutos</strong>.
                  </p>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Si no recibes el email en unos minutos:
                </p>
                <ul className="text-xs text-gray-500 text-left space-y-1 mb-4 max-w-sm mx-auto">
                  <li>• Revisa tu carpeta de spam o correo no deseado</li>
                  <li>• Verifica que el email sea correcto</li>
                  <li>• Intenta solicitar un nuevo enlace</li>
                </ul>
              </div>
              <div className="space-y-2">
                <button
                  onClick={resetForgotPassword}
                  className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Volver al login
                </button>
                <button
                  onClick={() => {
                    setForgotPasswordSent(false);
                    setError('');
                  }}
                  className="w-full py-2 px-4 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Reenviar email
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    required
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Enviando...
                    </>
                  ) : (
                    'Enviar instrucciones'
                  )}
                </button>

                <button
                  type="button"
                  onClick={resetForgotPassword}
                  className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Volver al login
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        {/* Logo y título */}
        <div className="text-center mb-8">
          <img 
            src="/logo-ceroideas.png" 
            alt="Cero Ideas Logo" 
            className="w-16 h-16 mx-auto mb-4 rounded-xl shadow-sm"
          />
          <h1 className="text-2xl font-bold text-gray-900">Solicitar Acceso</h1>
          <p className="text-gray-600 mt-2">Solicita acceso al CRM - Requiere aprobación</p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                placeholder="tu@email.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                Recordarme
              </label>
            </div>

            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(true);
                setError('');
              }}
              className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Iniciando sesión...
              </>
            ) : (
              'Iniciar sesión'
            )}
          </button>
        </form>

        {/* Register Link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            ¿No tienes cuenta?{' '}
            <button
              onClick={() => {
                setShowRegister(true);
                setError('');
              }}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Solicitar acceso
            </button>
          </p>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            {APP_BRAND.copyright}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;